/**
 * Loans table for the Financing page. One row per owned property; rows whose
 * financing is disabled render as "All Cash" / "Not set". Clicking any row
 * opens the LoanEditDrawer (provided by the parent).
 *
 * Layout matches `design/Syntra/financing.html` — Property · Lender ·
 * Loan Type badge · Loan Amount · Rate · Maturity · Monthly Payment Date · Monthly.
 */
import { Banknote, RefreshCw, Wallet, Zap } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { MockProperty } from "@/data/mockProperties";
import type { Strategy } from "@/types/pipeline";

import {
  fmtMaturityDate,
  maturityRemaining,
  nextEmiDate,
  type LoanBadgeTone,
  type OwnedLoanInfo,
} from "./financing-metrics";

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(value)).toLocaleString();
}

function fmtPct(value: number, digits = 2): string {
  return `${value.toFixed(digits)}%`;
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .split(" ")
    .map((word) => {
      if (word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, ""))) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

const BADGE_TONE: Record<LoanBadgeTone, string> = {
  hard: "bg-warning/15 text-warning",
  refi: "bg-cyan-500/15 text-cyan-500",
  conv: "bg-blue-500/15 text-blue-500",
  cash: "bg-muted text-muted-foreground",
  other: "bg-muted text-muted-foreground",
};

const BADGE_ICON: Record<LoanBadgeTone, typeof Banknote> = {
  hard: Zap,
  refi: RefreshCw,
  conv: Banknote,
  cash: Wallet,
  other: Banknote,
};

export interface FinancingTableRow {
  /** pipeline_properties.id — React key + click target. */
  pipelinePropertyId: string;
  info: OwnedLoanInfo;
  property: MockProperty;
  strategy: Strategy | null;
}

interface FinancingLoansTableProps {
  rows: FinancingTableRow[];
  onRowClick: (row: FinancingTableRow) => void;
}

const TH =
  "text-[11px] font-bold uppercase tracking-wider text-muted-foreground";

export function FinancingLoansTable({
  rows,
  onRowClick,
}: FinancingLoansTableProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className={TH}>Property</TableHead>
            <TableHead className={TH}>Lender</TableHead>
            <TableHead className={TH}>Loan Type</TableHead>
            <TableHead className={cn(TH, "text-right")}>Loan Amount</TableHead>
            <TableHead className={cn(TH, "text-right")}>Rate</TableHead>
            <TableHead className={cn(TH, "text-right")}>Maturity</TableHead>
            <TableHead className={cn(TH, "text-right")}>Monthly Payment Date</TableHead>
            <TableHead className={cn(TH, "text-right")}>Monthly Payment</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const { info } = row;
            const hasLoan = info.financed && info.loanAmount > 0;
            const BadgeIcon = BADGE_ICON[info.badge.tone];
            const emi = hasLoan ? nextEmiDate(info.loanDate) : null;
            const addr1 = titleCase(row.property.address) || "Property";
            const addr2 = [row.property.city, row.property.state]
              .filter(Boolean)
              .join(", ");

            return (
              <TableRow
                key={row.pipelinePropertyId}
                onClick={() => onRowClick(row)}
                className="cursor-pointer transition hover:bg-muted/40"
              >
                <TableCell className="py-3.5">
                  <div className="text-[13.5px] font-bold text-foreground">
                    {addr1}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {addr2}
                    {row.strategy && (
                      <>
                        {" · "}
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                          {row.strategy}
                        </span>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell
                  className={cn(
                    "py-3.5 text-[13.5px]",
                    info.lender
                      ? "text-foreground"
                      : "italic text-muted-foreground/70",
                  )}
                >
                  {info.lender || "Not set"}
                </TableCell>
                <TableCell className="py-3.5">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap",
                      BADGE_TONE[info.badge.tone],
                    )}
                  >
                    <BadgeIcon className="h-3 w-3" aria-hidden />
                    {info.badge.label}
                  </span>
                </TableCell>
                <TableCell className="py-3.5 text-right text-[13.5px] font-bold tabular-nums">
                  {hasLoan ? fmtCurrency(info.loanAmount) : "—"}
                </TableCell>
                <TableCell className="py-3.5 text-right text-[13.5px] tabular-nums">
                  {hasLoan ? fmtPct(info.rate) : "—"}
                </TableCell>
                <TableCell className="py-3.5 text-right text-[13.5px] text-muted-foreground tabular-nums">
                  {hasLoan ? maturityRemaining(info.loanDate, info.termMonths) : "—"}
                </TableCell>
                <TableCell className="py-3.5 text-right text-[13.5px] text-muted-foreground tabular-nums">
                  {hasLoan ? fmtMaturityDate(emi) : "—"}
                </TableCell>
                <TableCell className="py-3.5 text-right">
                  <span
                    className={cn(
                      "text-[13.5px] font-bold tabular-nums",
                      hasLoan && info.monthly > 0
                        ? "text-destructive"
                        : "text-muted-foreground",
                    )}
                  >
                    {hasLoan && info.monthly > 0
                      ? `${fmtCurrency(info.monthly)}/mo`
                      : "—"}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
